/**
 * 45. Commission Dashboard — sales agent commissions and payout status.
 *
 * Arabic and English (C7): every word from the catalogs; statuses agree with
 * the commission.
 */
import { Card, PageHeader, DataTable, Skeleton, Badge } from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { statusLabel } from "@/lib/i18n/records";
import { formatMoney } from "@/lib/money";

interface CommissionRow {
  id: string;
  agentName: string;
  period: string;
  earnedAmount: string;
  paidAmount: string;
  currencyCode: string;
  status: "Accrued" | "Approved" | "Paid";
}

export function CommissionsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<CommissionRow>(
    ["sales", "commissions"],
    "/sales/commissions",
  );

  return (
    <div>
      <PageHeader
        title={t("sales.commissions.title")}
        subtitle={t("sales.commissions.subtitle")}
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("sales.commissions.list"), data.total)}>
          <DataTable<CommissionRow>
            rows={data.items}
            columns={[
              { key: "agentName", label: t("sales.commissions.agent") },
              { key: "period", label: t("fields.period") },
              {
                key: "earnedAmount",
                label: t("sales.commissions.earned"),
                numeric: true,
                render: (r) => formatMoney(r.earnedAmount, r.currencyCode),
              },
              {
                key: "paidAmount",
                label: t("sales.commissions.paid"),
                numeric: true,
                render: (r) => formatMoney(r.paidAmount, r.currencyCode),
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) => (
                  <Badge
                    tone={
                      r.status === "Paid"
                        ? "success"
                        : r.status === "Approved"
                          ? "primary"
                          : "warning"
                    }
                  >
                    {statusLabel(t, "Commission", r.status)}
                  </Badge>
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
